import { useState, useEffect } from "react";
import { Mail, MessageSquare, Copy, Eye, Zap, CalendarCheck, Sparkles, RefreshCw, PenLine, Clock, Send, Pencil, Reply, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { painPoint, revenueBand } from "../lib/outreach";
import { sendEmail, connectGmail } from "../lib/gmail";

export default function ActionCard({ prospect, type, onView, onSent, onGenerated, onUpdated, onLogFollowUp }) {
  const p = prospect;
  const [logging, setLogging] = useState(false);
  const logFollowUp = async () => {
    if (!onLogFollowUp) return;
    setLogging(true);
    try { await onLogFollowUp(p); } finally { setLogging(false); }
  };
  const [copy, setCopy] = useState(p.outreach_copy?.[type] || null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState(p.email || "");
  const [draftEmail, setDraftEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(copy?.subject || "");
  const [bodyText, setBodyText] = useState(copy?.body || "");
  const [smsText, setSmsText] = useState(copy?.sms || "");
  const hasThread = !!p.email_thread?.thread_id;
  useEffect(() => { setSubject(copy?.subject || ""); setBodyText(copy?.body || ""); setSmsText(copy?.sms || ""); }, [copy]);
  const isSeq = ["book", "close_deal"].includes(type);
  const nextTouch = (p.follow_up_step || 0) + 1;
  const textMode = isSeq && nextTouch >= 5;
  const pain = painPoint(p);
  const rev = revenueBand(p);
  const when = p.call_time
    ? new Date(p.call_time).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  const draft = async (regenerate = false) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/prospects/${p.id}/outreach`, { type, regenerate });
      setCopy(data.copy);
      onGenerated && onGenerated(p.id, type, data.copy);
      if (regenerate) toast.success("Fresh draft ready");
    } catch {
      toast.error("Couldn't draft the reply — try again");
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const sendEmailNow = async () => {
    setSending(true);
    try {
      await sendEmail({ prospect_id: p.id, to: email, subject, body: bodyText });
      toast.success("Email sent from your Gmail");
      onSent && onSent(p);
    } catch (e) {
      if (e?.response?.status === 428) { toast.message("Connect your Gmail to send from PIT"); connectGmail(); }
      else toast.error(e?.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  const saveEmail = async () => {
    const val = draftEmail.trim();
    if (!val.includes("@")) { toast.error("Enter a valid email"); return; }
    setSavingEmail(true);
    try {
      const { data } = await api.patch(`/prospects/${p.id}`, { email: val });
      setEmail(val);
      onUpdated && onUpdated(data);
      toast.success("Email saved");
    } catch {
      toast.error("Could not save email");
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors p-4" data-testid={`action-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-white truncate">{p.name}</p>
            {p.reply_received && (
              <span className="text-[9px] font-mono-tech uppercase text-rose-200 bg-rose-500/20 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`card-replied-badge-${p.id}`}><Reply size={10} /> Replied</span>
            )}
            {p.self_scheduled && (
              <span className="text-[9px] font-mono-tech uppercase text-amber-400 flex items-center gap-1"><Zap size={10} /> self-booked</span>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate">{p.company}{rev ? ` · ${rev}` : ""}</p>
          {p.sent_emails?.length > 0 && (
            <p className="text-[10px] font-mono-tech uppercase tracking-wider text-emerald-400/90 mt-1 flex items-center gap-1" data-testid={`card-sent-badge-${p.id}`}>
              <Send size={10} /> Sent {new Date(p.sent_emails[p.sent_emails.length - 1].sent_at).toLocaleDateString([], { month: "short", day: "numeric" })} · awaiting reply
            </p>
          )}
          {when && (
            <p className="text-xs text-[#9cc6f0] font-mono-tech mt-1 flex items-center gap-1"><CalendarCheck size={11} /> {when}</p>
          )}
          {pain && (
            <p className="text-xs text-slate-400 mt-1.5"><span className="text-slate-500">Wants help with:</span> {pain}</p>
          )}
          {isSeq && p.next_follow_up_at && (() => {
            const d = new Date(p.next_follow_up_at);
            const t = new Date(); t.setHours(0, 0, 0, 0);
            const due = new Date(d); due.setHours(0, 0, 0, 0);
            const overdue = due < t;
            const step = Math.min((p.follow_up_step || 0) + 1, 8);
            const chan = step >= 5 ? "Text" : "Email";
            return (
              <p className={`text-xs mt-1.5 font-mono-tech flex items-center gap-1 ${overdue ? "text-rose-400" : "text-amber-300"}`} data-testid={`card-followup-due-${p.id}`}>
                <Clock size={11} /> Follow-up {overdue ? "overdue" : "due"} {d.toLocaleDateString([], { month: "short", day: "numeric" })} · Touch {step} of 8 · {chan}
              </p>
            );
          })()}
        </div>
        <button data-testid={`card-view-btn-${p.id}`} onClick={() => onView(p)} className="shrink-0 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5" title="View details">
          <Eye size={15} />
        </button>
      </div>

      {isSeq && !textMode && onLogFollowUp && (
        <button
          data-testid={`card-log-followup-${p.id}`}
          onClick={logFollowUp}
          disabled={logging}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold transition-colors disabled:opacity-50"
          title="Use this when you followed up outside PIT (e.g. in another thread)"
        >
          <Check size={12} /> {logging ? "Logging…" : "I followed up — mark done & schedule next"}
        </button>
      )}

      {type === "close" && p.research && !p.research.error && (
        <div className="mt-2.5 rounded-md bg-[#4690d9]/[0.06] border border-[#4690d9]/20 p-2.5" data-testid={`card-brief-${p.id}`}>
          <p className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] mb-1 flex items-center gap-1"><Sparkles size={11} /> Pre-call brief</p>
          <p className="text-xs text-slate-300 max-h-24 overflow-y-auto whitespace-pre-line">{p.research.summary}</p>
        </div>
      )}
      {type === "close" && p.research_status === "pending" && (
        <p className="mt-2 text-[11px] text-slate-500">Preparing briefing…</p>
      )}

      <div className="mt-3 rounded-md bg-black/25 border border-white/[0.06] p-2.5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 flex items-center gap-1"><Sparkles size={10} /> {textMode ? "AI text" : "AI reply"}</p>
          {copy && (
            <div className="flex items-center gap-2">
              <button
                data-testid={`card-edit-btn-${p.id}`}
                onClick={() => setEditing(!editing)}
                className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400 hover:text-white flex items-center gap-1"
              >
                <Pencil size={10} /> {editing ? "Done" : "Edit"}
              </button>
              <button
                data-testid={`card-regen-btn-${p.id}`}
                onClick={() => draft(true)}
                disabled={loading}
                className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] hover:text-white disabled:opacity-50 flex items-center gap-1"
              >
                <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Regenerate
              </button>
            </div>
          )}
        </div>
        {loading && !copy ? (
          <p className="text-xs text-slate-400 py-1">Drafting a personalized reply…</p>
        ) : copy ? (
          editing ? (
            <div className="space-y-1.5">
              {!textMode && !hasThread && (
                <input
                  data-testid={`card-subject-input-${p.id}`}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]"
                />
              )}
              <textarea
                data-testid={`card-body-input-${p.id}`}
                value={textMode ? smsText : bodyText}
                onChange={(e) => (textMode ? setSmsText(e.target.value) : setBodyText(e.target.value))}
                rows={textMode ? 3 : 6}
                className="w-full bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-[#4690d9] resize-y"
              />
            </div>
          ) : (
            <>
              {textMode ? (
                <p className="text-[10px] text-slate-500 mb-1">Text message · copy it to your phone to send</p>
              ) : hasThread ? (
                <p className="text-[10px] text-slate-500 mb-1">↳ Continues the existing email thread</p>
              ) : (
                subject && <p className="text-[10px] text-slate-500 mb-1">Subject: <span className="text-slate-300">{subject}</span></p>
              )}
              <p className="text-xs text-slate-300 whitespace-pre-line max-h-24 overflow-y-auto" data-testid={`card-copy-body-${p.id}`}>{textMode ? smsText : bodyText}</p>
            </>
          )
        ) : (
          <button
            data-testid={`card-draft-btn-${p.id}`}
            onClick={() => draft(false)}
            disabled={loading}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-xs font-semibold transition-colors disabled:opacity-50"
          >
            <PenLine size={12} /> Draft AI reply
          </button>
        )}
      </div>

      {copy && (textMode ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button
            data-testid={`card-copy-btn-${p.id}`}
            onClick={() => copyText(smsText, "Text")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#003b8d] hover:bg-[#0a4aa0] text-white text-xs font-semibold transition-colors"
          >
            <Copy size={12} /> Copy text
          </button>
          {onLogFollowUp && (
            <button
              data-testid={`card-mark-texted-${p.id}`}
              onClick={logFollowUp}
              disabled={logging}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold transition-colors disabled:opacity-50"
            >
              <Check size={12} /> {logging ? "Logging…" : "Mark as texted → next"}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-2.5 space-y-2">
          {!email && (
            <div className="flex gap-2" data-testid={`card-add-email-${p.id}`}>
              <input
                data-testid={`card-email-input-${p.id}`}
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveEmail()}
                placeholder="Add email to send…"
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]"
              />
              <button
                data-testid={`card-save-email-${p.id}`}
                onClick={saveEmail}
                disabled={savingEmail}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold transition-colors disabled:opacity-60"
              >
                Save
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {email && (
              <button
                data-testid={`card-send-btn-${p.id}`}
                onClick={sendEmailNow}
                disabled={sending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold transition-colors disabled:opacity-60"
              >
                {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />} {sending ? "Sending…" : "Send"}
              </button>
            )}
            {p.phone && (
              <a
                data-testid={`card-sms-btn-${p.id}`}
                href={`sms:${p.phone}?&body=${encodeURIComponent(copy.sms)}`}
                onClick={() => onSent && onSent(p)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#003b8d] hover:bg-[#0a4aa0] text-white text-xs font-semibold transition-colors"
              >
                <MessageSquare size={12} /> Text
              </a>
            )}
            <button
              data-testid={`card-copy-btn-${p.id}`}
              onClick={() => copyText(bodyText, "Message")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-xs font-semibold transition-colors"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
